import type { ThemePaletteStyle } from '@/data/whiteLabelThemeCatalog'
import type { AppThemeConfig } from '@/utils/theme'

const LIGHT_CANVAS = '#f8fafc'
const LIGHT_CARD = '#ffffff'
const LIGHT_TEXT = '#1e293b'
const LIGHT_TEXT_MUTED = '#64748b'

const DARK_CANVAS = '#0f172a'
const DARK_CARD = '#1e293b'
const DARK_TEXT = '#f1f5f9'
const DARK_TEXT_MUTED = '#94a3b8'

function hexToRgb(hex: string): { r: number; g: number; b: number } | null {
  const clean = hex.replace('#', '').trim()
  if (!/^[\da-fA-F]{6}$/.test(clean)) return null
  return {
    r: parseInt(clean.slice(0, 2), 16),
    g: parseInt(clean.slice(2, 4), 16),
    b: parseInt(clean.slice(4, 6), 16),
  }
}

function relativeLuminance(hex: string): number {
  const rgb = hexToRgb(hex)
  if (!rgb) return 0.5
  const channel = (c: number) => {
    const s = c / 255
    return s <= 0.03928 ? s / 12.92 : ((s + 0.055) / 1.055) ** 2.4
  }
  return 0.2126 * channel(rgb.r) + 0.7152 * channel(rgb.g) + 0.0722 * channel(rgb.b)
}

export function isLightColor(hex: string): boolean {
  return relativeLuminance(hex) > 0.45
}

function clampByte(n: number): number {
  return Math.max(0, Math.min(255, Math.round(n)))
}

function rgbToHex(r: number, g: number, b: number): string {
  return `#${[r, g, b].map((c) => clampByte(c).toString(16).padStart(2, '0')).join('')}`
}

function mixHex(a: string, b: string, weightB: number): string {
  const ca = hexToRgb(a)
  const cb = hexToRgb(b)
  if (!ca || !cb) return a
  const w = Math.max(0, Math.min(1, weightB))
  return rgbToHex(
    ca.r + (cb.r - ca.r) * w,
    ca.g + (cb.g - ca.g) * w,
    ca.b + (cb.b - ca.b) * w
  )
}

function adjustHex(hex: string, amount: number): string {
  const rgb = hexToRgb(hex)
  if (!rgb) return hex
  return rgbToHex(rgb.r + amount, rgb.g + amount, rgb.b + amount)
}

function hexToRgba(hex: string, alpha: number): string | null {
  const rgb = hexToRgb(hex)
  if (!rgb) return null
  return `rgba(${rgb.r}, ${rgb.g}, ${rgb.b}, ${alpha})`
}

/** Escolhe cor de acção com contraste útil para botões e links. */
function resolveAccentColor(primaria: string, secundaria: string, destaque: string): string {
  const idealMin = 0.22
  const idealMax = 0.58
  const candidates = [destaque, secundaria, primaria]

  for (const color of candidates) {
    const lum = relativeLuminance(color)
    if (lum >= idealMin && lum <= idealMax) return color
  }

  const base = destaque
  const lum = relativeLuminance(base)
  if (lum > idealMax) return mixHex(base, '#1e293b', 0.52)
  if (lum < idealMin) return adjustHex(base, 56)
  return base
}

function buildLightSurfaces(primaria: string, secundaria: string) {
  const bg = mixHex(primaria, LIGHT_CANVAS, 0.78)
  const bgCard = mixHex(secundaria, LIGHT_CARD, 0.9)
  const bgElevated = mixHex(secundaria, mixHex(primaria, LIGHT_CARD, 0.7), 0.58)

  const text = mixHex(LIGHT_TEXT, primaria, 0.18)
  const textMuted = mixHex(LIGHT_TEXT_MUTED, mixHex(primaria, secundaria, 0.5), 0.22)

  return { bg, bgCard, bgElevated, text, textMuted }
}

function buildDarkSurfaces(primaria: string, secundaria: string) {
  const bg = mixHex(primaria, DARK_CANVAS, 0.7)
  const bgCard = mixHex(mixHex(primaria, DARK_CARD, 0.5), secundaria, 0.24)
  const bgElevated = mixHex(bgCard, secundaria, 0.2)

  const text = mixHex(DARK_TEXT, secundaria, 0.1)
  const textMuted = mixHex(DARK_TEXT_MUTED, mixHex(secundaria, primaria, 0.5), 0.28)

  return { bg, bgCard, bgElevated, text, textMuted }
}

/**
 * Converte paleta do catálogo em tema aplicável.
 * Superfícies neutras com toque de marca; cor forte só em acentos (regra 60-30-10 na prática).
 */
export function buildThemeFromPalette(
  style: ThemePaletteStyle,
  catalogKey?: string
): AppThemeConfig {
  const { primaria, secundaria, destaque } = style
  const darkUi = !isLightColor(primaria)

  const surfaces = darkUi
    ? buildDarkSurfaces(primaria, secundaria)
    : buildLightSurfaces(primaria, secundaria)

  const accent = resolveAccentColor(primaria, secundaria, destaque)
  const accentHover = darkUi ? adjustHex(accent, 18) : adjustHex(accent, -14)
  const accentForeground = textOnAccent(accent)

  const border = hexToRgba(accent, darkUi ? 0.26 : 0.18) ?? 'rgba(148, 163, 184, 0.2)'
  const borderSubtle = hexToRgba(accent, darkUi ? 0.15 : 0.1) ?? 'rgba(148, 163, 184, 0.1)'

  return {
    ...surfaces,
    accent,
    accentHover,
    accentForeground,
    border,
    borderSubtle,
    radius: '16px',
    radiusSm: '10px',
    catalogKey,
  }
}

/** Cor de texto legível sobre fundo de acento (botões). */
export function textOnAccent(accent: string): string {
  return isLightColor(accent) ? LIGHT_TEXT : '#ffffff'
}

/** Suaviza temas antigos guardados sem catálogo (fundos saturados). */
export function refineStoredTheme(theme: AppThemeConfig): AppThemeConfig {
  const darkUi = !isLightColor(theme.bg)
  const canvas = darkUi ? DARK_CANVAS : LIGHT_CANVAS
  const card = darkUi ? DARK_CARD : LIGHT_CARD

  let accent = theme.accent
  const accentLum = relativeLuminance(accent)
  if (accentLum > 0.58) accent = mixHex(accent, '#1e293b', 0.52)
  else if (accentLum < 0.22) accent = adjustHex(accent, 56)

  const accentHover = theme.accentHover || (darkUi ? adjustHex(accent, 18) : adjustHex(accent, -14))

  return {
    bg: mixHex(theme.bg, canvas, 0.82),
    bgCard: mixHex(theme.bgCard, card, 0.9),
    bgElevated: mixHex(theme.bgElevated, mixHex(theme.bgCard, card, 0.55), 0.58),
    text: darkUi ? mixHex(DARK_TEXT, accent, 0.1) : mixHex(LIGHT_TEXT, accent, 0.14),
    textMuted: darkUi ? mixHex(DARK_TEXT_MUTED, accent, 0.16) : mixHex(LIGHT_TEXT_MUTED, accent, 0.18),
    accent,
    accentHover,
    accentForeground: textOnAccent(accent),
    border: hexToRgba(accent, darkUi ? 0.26 : 0.18) ?? theme.border,
    borderSubtle: hexToRgba(accent, darkUi ? 0.15 : 0.1) ?? theme.borderSubtle,
    radius: theme.radius,
    radiusSm: theme.radiusSm,
    catalogKey: theme.catalogKey,
    customPalette: theme.customPalette,
  }
}
